import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, watch } from 'vue';
import { useColor } from '@/composables/color';
import ColorPropertyItem from './ColorPropertyItem.vue';
import { ColorPropertyType } from './types';
import type {
    FontColorOption,
    ImagePlaceholderElement,
    WordSearchConfig
} from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';

interface ColorConfig {
    label: string;
    configKey: ColorPropertyType;
    defaultValue: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ColorProperties',
  props: {
    imageElement: null,
    editMode: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
    imageElement?: ImagePlaceholderElement;
    editMode?: boolean;
};

const COLOR_CONFIGS: ColorConfig[] = [
    { label: 'Filler Letters Color', configKey: ColorPropertyType.FillerColor, defaultValue: '#EBF9FA' },
    { label: 'Word Color', configKey: ColorPropertyType.WordColor, defaultValue: '#7258E0' },
    { label: 'Highlight Color', configKey: ColorPropertyType.HighlightColor, defaultValue: '#EDE9FB' },
    { label: 'Grid Color', configKey: ColorPropertyType.GridColor, defaultValue: '#EBF9FA' },
    { label: 'Background Color', configKey: ColorPropertyType.BackgroundColor, defaultValue: '#FFFFFF00' },
    { label: 'Highlight Outline', configKey: ColorPropertyType.HighlightStrokeColor, defaultValue: '#7258E0' }
];

const DEFAULT_HIGHLIGHT_STROKE_WIDTH = 2;



const model = reactive({
    fillerColor: undefined as ColorObject | undefined,
    wordColor: undefined as ColorObject | undefined,
    gridColor: undefined as ColorObject | undefined,
    highlightColor: undefined as ColorObject | undefined,
    highlightStrokeColor: undefined as ColorObject | undefined,
    highlightStrokeWidth: 0,
    highlightStrokeEnabled: false,
    backgroundColor: undefined as ColorObject | undefined
});

const { getColorFromString, hexToHexa } = useColor();

const parseColorFromConfig = (colorValue: string | undefined, defaultHex: string): ColorObject => {
    if (!colorValue) return { hex: defaultHex, alpha: 1, cmyk: '' };

    const color = getColorFromString(colorValue);
    return { hex: color.hex, alpha: color.a, cmyk: '' };
};

watch(
    () => props.imageElement,
    (element) => {
        if (!element || !element.wordSearchConfig) return;

        const config = element.wordSearchConfig;
        COLOR_CONFIGS.forEach(({ configKey, defaultValue }) => {
            model[configKey] = parseColorFromConfig(config[configKey], defaultValue);
        });
        model.highlightStrokeWidth = config.highlightStrokeWidth ?? 0;
        model.highlightStrokeEnabled = !!config.highlightStrokeColor || (config.highlightStrokeWidth ?? 0) > 0;
    },
    { immediate: true }
);

const disabled = computed(() => !!props.imageElement?.locked);

const updateWordSearchConfig = (config: Partial<WordSearchConfig>) => {
    const element = props.imageElement;
    if (!element) return;

    const newConfig = { ...element.wordSearchConfig, ...config } as WordSearchConfig;
    element.set('wordSearchConfig', newConfig);
    if (props.editMode) element.setEditedProp('wordSearchConfig');
};

const onColorSelected = (configKey: ColorPropertyType, color: FontColorOption) => {
    model[configKey] = { ...color } as ColorObject;
    updateWordSearchConfig({
        [configKey]: hexToHexa(model[configKey]!.hex, model[configKey]!.alpha)
    });
};

const clampWidth = (value: number): number => Math.max(0, Math.min(10, value));

const updateHighlightStrokeWidth = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
    const clampedValue = clampWidth(numValue);
    model.highlightStrokeWidth = clampedValue;
    updateWordSearchConfig({ highlightStrokeWidth: clampedValue });
};

const toggleHighlightStroke = () => {
    model.highlightStrokeEnabled = !model.highlightStrokeEnabled;

    if (model.highlightStrokeEnabled) {
        const defaultColor = parseColorFromConfig(undefined, '#7258E0');
        model.highlightStrokeColor = defaultColor;
        model.highlightStrokeWidth = DEFAULT_HIGHLIGHT_STROKE_WIDTH;
        updateWordSearchConfig({
            highlightStrokeColor: hexToHexa(defaultColor.hex, defaultColor.alpha),
            highlightStrokeWidth: DEFAULT_HIGHLIGHT_STROKE_WIDTH
        });
    } else {
        model.highlightStrokeWidth = 0;
        model.highlightStrokeColor = undefined;
        updateWordSearchConfig({
            highlightStrokeColor: undefined,
            highlightStrokeWidth: 0
        });
    }
};

return { __sfc: true,COLOR_CONFIGS, DEFAULT_HIGHLIGHT_STROKE_WIDTH, props, model, getColorFromString, hexToHexa, parseColorFromConfig, disabled, updateWordSearchConfig, onColorSelected, clampWidth, updateHighlightStrokeWidth, toggleHighlightStroke, ColorPropertyItem }
}

})